import type { CatalogItem, CheckResult, ClientDetail, ClientSummary, LastVisit, Slot, VisitServiceRequest } from "@vaulted/shared";
import { ageOn, formatCents, formatPhone, parseDollars } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { errorMessage, get, post } from "../api";
import { useCatalog } from "../pages/Catalog";
import { ClientPicker } from "../pages/ClientProfile";
import { displayName, fmtDob } from "../pages/Clients";
import { useSession } from "../session";
import { ServicesPicker } from "./ServicesPicker";
import { Alert, Button, Checkbox, Dialog, Field, Loading, SelectField, cx } from "./ui";

interface GuestDraft {
  client: Pick<ClientSummary, "id" | "firstName" | "lastName" | "preferredName" | "dob" | "mobile">;
  services: VisitServiceRequest[];
  staffId: string;
  price: string;
  priceReason: string;
  servicesReason: string;
}

export interface BookingPrefill {
  date: string;
  time?: string;
  staffId?: string;
  client?: ClientSummary | ClientDetail;
  walkIn?: boolean;
}

/** Book one or more guests: who, what, when. Opens from the calendar, the front desk, or a client's profile. */
export function BookingDialog({ prefill, onClose, onBooked, staff }: { prefill: BookingPrefill; onClose: () => void; onBooked?: () => void; staff: { id: string; name: string }[] }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const catalog = useCatalog();
  const [booker, setBooker] = useState<GuestDraft["client"] | null>(prefill.client ?? null);
  const [guests, setGuests] = useState<GuestDraft[]>([]);
  const [picking, setPicking] = useState<"booker" | "guest" | null>(prefill.client ? null : "booker");
  const [date, setDate] = useState(prefill.date);
  const [time, setTime] = useState(prefill.time ?? "");
  const [forceReason, setForceReason] = useState("");
  const [notes, setNotes] = useState("");

  const household = useQuery({
    queryKey: ["client", booker?.id],
    queryFn: () => get<ClientDetail>(`/api/clients/${booker!.id}`),
    enabled: !!booker,
    staleTime: 60_000,
  });

  const services = (catalog.data?.items ?? []).filter((i) => i.type === "service" && i.status === "active" && i.availableClinic);
  // Nothing pre-chosen: staff pick the service (their last visit is offered as one tap).
  const newGuest = (client: GuestDraft["client"]): GuestDraft => ({ client, services: [], staffId: prefill.staffId ?? "", price: "", priceReason: "", servicesReason: "" });

  // The booker is the first guest unless removed.
  useEffect(() => {
    if (booker && guests.length === 0 && services.length) setGuests([newGuest(booker)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booker, services.length]);

  const setGuest = (i: number, patch: Partial<GuestDraft>) => setGuests((cur) => cur.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  const requests = guests.map((g) => ({ services: g.services, staffId: g.staffId || null }));
  const ready = !!booker && guests.length > 0 && guests.every((g) => g.services.length > 0);

  const slots = useQuery({
    queryKey: ["slots", date, JSON.stringify(requests)],
    queryFn: () => post<Slot[]>("/api/bookings/slots", { date, guests: requests, includeOutsideHours: false }),
    enabled: ready && !!date,
  });
  const check = useQuery({
    queryKey: ["check", date, time, JSON.stringify(requests)],
    queryFn: () => post<CheckResult>("/api/bookings/check", { start: `${date}T${time}`, guests: requests }),
    enabled: ready && !!date && /^\d\d:\d\d$/.test(time),
  });

  const book = useMutation({
    mutationFn: () =>
      post("/api/bookings", {
        bookerClientId: booker!.id,
        source: prefill.walkIn ? "walk_in" : "staff",
        start: `${date}T${time}`,
        notes,
        forceReason: forceReason || null,
        guests: guests.map((g, i) => ({
          clientId: g.client.id,
          services: g.services,
          staffId: g.staffId || check.data?.guests[i]?.staffId || null,
          priceCents: g.price ? parseDollars(g.price) : null,
          priceReason: g.priceReason,
          servicesReason: g.servicesReason || undefined,
        })),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["calendar"] });
      await qc.invalidateQueries({ queryKey: ["client-appointments"] });
      onBooked?.();
      onClose();
    },
  });

  const householdOptions = useMemo(() => {
    const d = household.data;
    if (!d) return [];
    return d.links.map((l) => ({ ...l.other, preferredName: null })).filter((o) => !guests.some((g) => g.client.id === o.id));
  }, [household.data, guests]);

  if (catalog.isLoading) return null;

  return (
    <Dialog
      title={prefill.walkIn ? "Walk-in" : "Book"}
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={book.isPending}
            disabled={!ready || !time || (check.data && !check.data.ok && !forceReason)}
            onClick={() => book.mutate()}
          >
            {check.data && !check.data.ok ? "Book anyway" : "Book"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {/* Who */}
        <section>
          <h3 className="mb-2 text-sm font-semibold text-ink-soft">Booked by</h3>
          {booker && picking !== "booker" ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius-control)] border border-line px-3 py-2">
              <span>
                <span className="font-semibold">{displayName(booker)}</span>{" "}
                <span className="text-sm text-muted">
                  {fmtDob(booker.dob)}
                  {booker.mobile && ` · ${formatPhone(booker.mobile)}`}
                </span>
              </span>
              <button className="text-sm font-semibold text-brand" onClick={() => (setPicking("booker"), setGuests([]))}>
                Change
              </button>
            </div>
          ) : (
            <ClientPicker excludeId="" onPick={(c) => (setBooker(c), setGuests([]), setPicking(null))} />
          )}
        </section>

        {/* Guests and services */}
        {booker && (
          <section className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold text-ink-soft">Guests and services</h3>
            {guests.map((g, i) => {
              const price = check.data?.prices[i];
              return (
                <div key={g.client.id} className="rounded-[var(--radius-control)] border border-line p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="font-semibold">
                      {displayName(g.client)} <span className="text-sm font-normal text-muted">{g.client.dob ? `age ${ageOn(g.client.dob, date || new Date().toISOString().slice(0, 10))}` : "birth date needed"}</span>
                    </span>
                    {guests.length > 1 && (
                      <button className="text-sm font-semibold text-muted hover:text-danger" onClick={() => setGuests((cur) => cur.filter((_, j) => j !== i))}>
                        Remove
                      </button>
                    )}
                  </div>
                  <GuestServices
                    clientId={g.client.id}
                    items={catalog.data?.items ?? []}
                    services={services}
                    value={g.services}
                    onChange={(list) => setGuest(i, { services: list })}
                    reason={g.servicesReason}
                    onReason={(r) => setGuest(i, { servicesReason: r })}
                  />
                  <SelectField label="Nurse" className="mt-2 max-w-sm" value={g.staffId} onChange={(e) => setGuest(i, { staffId: e.target.value })}>
                    <option value="">Any (suggested)</option>
                    {staff.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </SelectField>
                  {price && (
                    <p className="mt-2 text-sm text-ink-soft">
                      {formatCents(price.totalCents)}
                      {price.discountCents > 0 && ` (group discount ${formatCents(price.discountCents)})`}
                      {price.addOns.some((a) => a.included) && ` · included: ${price.addOns.filter((a) => a.included).map((a) => a.name).join(", ")}`}
                    </p>
                  )}
                  {can("prices.override") && (
                    <div className="mt-2 grid gap-3 sm:grid-cols-2">
                      <Field label="Price override (optional)" inputMode="decimal" placeholder={price ? (price.totalCents / 100).toFixed(2) : ""} value={g.price} onChange={(e) => setGuest(i, { price: e.target.value })} />
                      {g.price && <Field label="Reason" required value={g.priceReason} onChange={(e) => setGuest(i, { priceReason: e.target.value })} />}
                    </div>
                  )}
                </div>
              );
            })}
            {picking === "guest" ? (
              <div className="rounded-[var(--radius-control)] border border-line p-3">
                {householdOptions.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    <span className="text-sm text-muted">Family:</span>
                    {householdOptions.map((o) => (
                      <button key={o.id} className="rounded-full border border-line px-3 py-1 text-sm font-semibold hover:border-brand" onClick={() => (setGuests((cur) => [...cur, newGuest(o)]), setPicking(null))}>
                        {o.firstName} {o.lastName}
                      </button>
                    ))}
                  </div>
                )}
                <ClientPicker excludeId={booker.id} onPick={(c) => (setGuests((cur) => (cur.some((x) => x.client.id === c.id) ? cur : [...cur, newGuest(c)])), setPicking(null))} />
                <button className="mt-2 text-sm font-semibold text-muted" onClick={() => setPicking(null)}>
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3">
                <button className="text-sm font-semibold text-brand" onClick={() => setPicking("guest")}>
                  + Add a guest
                </button>
                {!guests.some((g) => g.client.id === booker.id) && (
                  <button className="text-sm font-semibold text-brand" onClick={() => setGuests((cur) => [newGuest(booker), ...cur])}>
                    + {booker.firstName} too
                  </button>
                )}
              </div>
            )}
          </section>
        )}

        {/* When */}
        {ready && (
          <section className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold text-ink-soft">When</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              <Field label="Start" type="time" step={300} value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
            {slots.isLoading ? (
              <Loading />
            ) : slots.data && slots.data.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {slots.data.map((s) => {
                  const t = s.start.slice(11, 16);
                  return (
                    <button
                      key={s.start}
                      onClick={() => setTime(t)}
                      className={cx("rounded-[var(--radius-control)] border px-2.5 py-1 text-sm tabular-nums", t === time ? "border-brand bg-brand text-white" : "border-line hover:border-brand")}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            ) : (
              slots.data && <p className="text-sm text-muted">No open times inside posted hours that day. You can still pick a time above.</p>
            )}
            {check.data &&
              (check.data.ok ? (
                <div className="rounded-[var(--radius-control)] bg-ok-wash px-3 py-2 text-sm text-ok">
                  {check.data.guests.map((g, i) => (
                    <div key={i}>
                      {guests[i]?.client.firstName}: {g.start.slice(11, 16)}–{g.end.slice(11, 16)} with {g.staffName}
                    </div>
                  ))}
                  {check.data.problems.map((p, i) => (
                    <div key={i} className="text-wait">
                      Note: {p.message}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-2 rounded-[var(--radius-control)] bg-danger-wash px-3 py-2 text-sm text-danger">
                  {[...new Set(check.data.problems.map((p) => p.message))].map((m) => (
                    <div key={m}>{m}</div>
                  ))}
                  <Field label="To book anyway (double-book), give a reason and pick a nurse for each guest" value={forceReason} onChange={(e) => setForceReason(e.target.value)} />
                </div>
              ))}
            <Field label="Booking note (optional)" maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </section>
        )}
        {book.error && <Alert>{errorMessage(book.error)}</Alert>}
      </div>
    </Dialog>
  );
}

/** A guest's services: the list to pick from, with their last visit offered first (if it's all still bookable here). */
function GuestServices({ clientId, items, services, value, onChange, reason, onReason }: { clientId: string; items: CatalogItem[]; services: CatalogItem[]; value: VisitServiceRequest[]; onChange: (v: VisitServiceRequest[]) => void; reason: string; onReason: (r: string) => void }) {
  const last = useQuery({ queryKey: ["last-visit", clientId], queryFn: () => get<LastVisit | null>(`/api/clients/${clientId}/last-visit`), staleTime: 60_000 });
  const offer = useMemo(() => {
    const v = last.data;
    if (!v?.services.length) return null;
    const ok = v.services.every((sv) => services.some((s) => s.id === sv.serviceId) && sv.addOnIds.every((id) => items.some((x) => x.id === id && x.status === "active")));
    if (!ok) return null;
    return { label: `Last visit (${new Date(`${v.date}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })})`, value: v.services };
  }, [last.data, services, items]);
  return <ServicesPicker items={items} services={services} value={value} onChange={onChange} price={(x) => x.priceCents} reason={reason} onReason={onReason} asList offer={offer} />;
}
